import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Label } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@egaranti/components";
import { RadioGroup, RadioGroupItem } from "@egaranti/components";
import { FileLink } from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import BaseProductLinkageService from "@/services/baseProductLinkageService";
import documentsService from "@/services/documentsService";

import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";
import CheckRole from "@/components/shared/checkRole";
import ProductLinkage from "@/components/shared/productLinkage";

import * as z from "zod";

const DocumentEditPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { toast } = useToast();
  const form = useForm({
    resolver: zodResolver(
      z.object({
        name: z.string().min(3, t("common.validateMsg.requiredField")),
        type: z.string().min(3, t("common.validateMsg.requiredField")),
        file: z.any().optional(),
      }),
    ),
    defaultValues: {
      name: "",
      type: "",
    },
  });

  const documentLinkageService = new BaseProductLinkageService(
    "/merchant-product-documents",
  );

  const onSubmit = (data) => {
    const dataToSend = {
      id: id,
      ...data,
    };

    const formData = new FormData();
    formData.append("multipartFile", data.file);
    documentsService
      .updateDocument(dataToSend, formData)
      .then(() => {
        toast({
          title: t("settingsPage.documentUpdatedSuccessfully"),
          variant: "success",
        });
      })
      .catch(() => {
        toast({
          title: err.response?.data?.errors[0]?.message,
          variant: "error",
        });
      });
  };

  useEffect(() => {
    documentsService.getDocument(id).then((res) => {
      form.setValue("name", res.name);
      form.setValue("documentUrl", res.documentUrl);
      form.setValue("type", res.type);
    });
  }, []);

  const handleLinkProducts = async (selectedIds) => {
    await documentsService.linkProducts(id, {
      merchantProductIds: selectedIds,
    });
  };

  const handleUnlinkProducts = async (selectedIds) => {
    await documentsService.unlinkProducts(id, {
      merchantProductIds: selectedIds,
    });
  };

  const handleLinkAll = async (filter) => {
    await documentsService.linkAllProducts(id, filter);
  };

  const handleUnlinkAll = async (filter) => {
    await documentsService.unlinkAllProducts(id, filter);
  };

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/settings/documents">
          {t("settingsPage.documents")}
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("settingsPage.editDocument")}</BreadcrumbItem>
      </Breadcrumb>
      <div className="container mx-auto my-20 px-4 sm:px-0 flex flex-col items-center justify-center gap-6">
        <div className="formBox">
          <h2 className="mb-5 font-medium">{t("settingsPage.documentInfo")}</h2>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("settingsPage.documentName")}</Label>
                    <FormControl>
                      <Input maxLength={50} placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("documentUrl") ? (
                <div className="flex flex-col gap-3">
                  <Label>{t("settingsPage.document")}</Label>
                  <FileLink
                    /* EPS-3961 — dosya adi kartin sag kenarini 175 px asiyordu
                       (mobil, olculen). FileLink'in basligi `truncate` tasimiyor
                       ve ara kaplari `min-w-0` olmadigi icin esnek kutu kucultemiyor.
                       DIKKAT: `min-w-0` yalniz METIN sarmalayicisina verilir. Ikon
                       sarmalayicisina da verilince esnek kutu indir/sil dugmelerini
                       44'ten 33 px'e siksiyordu (bagimsiz ajan olcumu); ikon kabi
                       `shrink-0` ile sabit tutulur.
                       Paket disaridan yalniz dis kabin sinifini aliyor; ic ogelere
                       rastgele degistirici ile ulasiliyor. */
                    className="min-w-0 [&>div:first-child]:min-w-0 [&>div:last-child]:shrink-0 [&_a]:h-11 [&_a]:w-11 [&_button]:h-11 [&_button]:w-11 [&_h2]:truncate"
                    onDeleted={() => form.setValue("documentUrl", null)}
                    name={form.watch("name")}
                    url={form.watch("documentUrl")}
                  />
                </div>
              ) : (
                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("settingsPage.document")}</Label>
                      <FormControl>
                        <Input
                          accept=".jpg, .jpeg, .png, .pdf"
                          type="file"
                          onChange={(e) => {
                            field.onChange(
                              e.target.files ? e.target.files[0] : null,
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("settingsPage.documentType")}</Label>
                    <FormControl>
                      <RadioGroup
                        className="flex py-5"
                        onValueChange={(value) => {
                          form.setValue("type", value);
                        }}
                        value={form.watch("type")}
                        {...field}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            disabled
                            value="USER_MANUAL_DOCUMENT"
                            id="USER_MANUAL_DOCUMENT"
                          />
                          <Label htmlFor="USER_MANUAL_DOCUMENT">
                            {t("settingsPage.userManual")}
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            disabled
                            value="WARRANTY_DOCUMENT"
                            id="WARRANTY_DOCUMENT"
                          />
                          <Label htmlFor="WARRANTY_DOCUMENT">
                            {t("settingsPage.warrantyDocument")}
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <CheckRole section="DOCUMENTS" action="EDIT">
          <div className="flex w-full justify-end">
            <Button onClick={form.handleSubmit(onSubmit)}>
              {t("common.save")}
            </Button>
          </div>
        </CheckRole>
        <ProductLinkage id={id} path="/merchant-product-documents" />
      </div>
    </main>
  );
};

export default DocumentEditPage;
